import { apiDestinationsClient } from '~/services';
import { ref, watch } from 'vue';
import { HTTP_UNPROCESSABLE_ENTITY } from '../../../../../support/HttpStatuses';
import { useToasted } from '../../../../../composables/useToasted';

const defaultData = () => ({ friendly_name: '', http_method: 'POST', uri_template: '', auth_config_id: '' });

export const useApiDestinationForm = () => {
    const toasted = useToasted();

    const teamId = Spark.state.currentTeam.id;

    const originalData = ref(defaultData());
    const data = ref(defaultData());
    const errors = ref({});
    const success = ref(false);
    const saving = ref(false);
    const loading = ref(false);

    watch(
        () => data.value?.id,
        async (id) => {
            errors.value = {};
            success.value = false;
            saving.value = false;
            loading.value = true;

            if (id) {
                originalData.value = (await apiDestinationsClient.get(teamId, id)).data;
                data.value = { ...originalData.value };
            } else {
                originalData.value = defaultData();
                data.value = defaultData();
            }
            loading.value = false;
        },
        { immediate: true }
    );

    const save = async () => {
        if (saving.value) {
            return;
        }

        errors.value = {};
        saving.value = true;
        try {
            if (data.value.id) {
                const changedData = Object.keys(data.value)
                    .filter(
                        (key) => data.value[key] !== originalData.value[key] && Object.keys(defaultData()).includes(key)
                    )
                    .reduce(
                        (changedData, key) => ({
                            ...changedData,
                            [key]: data.value[key],
                        }),
                        {}
                    );

                const response = (await apiDestinationsClient.update(teamId, data.value.id, changedData)).data;
                success.value = true;

                return response;
            } else {
                const response = (await apiDestinationsClient.create(teamId, data.value)).data;
                success.value = true;

                return response;
            }
        } catch (error) {
            success.value = false;
            if (error?.response?.status === HTTP_UNPROCESSABLE_ENTITY) {
                const responseErrors = error.response.data.errors;

                Object.keys(responseErrors).map((k) => {
                    errors.value[k] = responseErrors[k][0];
                });
            } else {
                toasted.global.platform_error();
            }
            throw error;
        } finally {
            saving.value = false;
        }
    };

    const duplicate = async (apiDestinationId) => {
        try {
            loading.value = true;
            data.value = {
                ...(await apiDestinationsClient.get(teamId, apiDestinationId)).data,
                friendly_name: '',
                id: undefined,
            };
        } catch (e) {
            console.error(e);
        } finally {
            loading.value = false;
        }
    };

    return { errors, saving, loading, save, data, success, duplicate };
};
